const toCount = (value) => {
    const count = Number(value);
    return Number.isFinite(count) ? count : 0;
};

const firstDefined = (source, keys) => {
    for (let index = 0; index < keys.length; index += 1) {
        const value = source[keys[index]];

        if (value !== undefined && value !== null) {
            return value;
        }
    }

    return undefined;
};

const unwrapData = (response = {}) => {
    if (Array.isArray(response)) {
        return response;
    }

    if (response.data !== undefined) {
        return response.data;
    }

    return response;
};

const unwrapList = (response, keys = []) => {
    const data = unwrapData(response);

    if (Array.isArray(data)) {
        return data;
    }

    if (!data || typeof data !== 'object') {
        return [];
    }

    for (let index = 0; index < keys.length; index += 1) {
        if (Array.isArray(data[keys[index]])) {
            return data[keys[index]];
        }
    }

    return [];
};

export const emptyPagination = () => ({
    total_items: 0,
    current_page: 1,
    total_pages: 1,
});

export const emptyReport = () => ({
    urls: [],
    series: [],
    totals: { widgetLoads: 0, conversations: 0 },
    pagination: emptyPagination(),
});

export const emptyFilterOptions = () => ({ accounts: [], widgets: [] });

export const paginateUrls = (urls = [], page = 1, perPage = urls.length || 1) => {
    const totalItems = urls.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / (perPage || 1)));
    const currentPage = Math.min(Math.max(1, page), totalPages);

    return {
        urls: urls.slice((currentPage - 1) * perPage, currentPage * perPage),
        pagination: {
            total_items: totalItems,
            current_page: currentPage,
            total_pages: totalPages,
        },
    };
};

const normalizeMetricRow = (row = {}) => ({
    url: firstDefined(row, ['url', 'page_url', 'current_url']) || '',
    widgetLoads: toCount(
        firstDefined(row, ['widgetLoads', 'widget_loads', 'loads', 'load_widget']),
    ),
    conversations: toCount(
        firstDefined(row, [
            'conversations',
            'widget_conversations',
            'start_conversation',
            'conversation_starts',
        ]),
    ),
});

const normalizeSeriesRow = (row = {}) => ({
    date: firstDefined(row, ['date', 'period', 'bucket', 'day']) || '',
    widgetLoads: toCount(
        firstDefined(row, ['widgetLoads', 'widget_loads', 'loads', 'load_widget']),
    ),
    conversations: toCount(
        firstDefined(row, [
            'conversations',
            'widget_conversations',
            'start_conversation',
            'conversation_starts',
        ]),
    ),
});

const sumMetrics = (rows) =>
    rows.reduce(
        (totals, row) => ({
            widgetLoads: totals.widgetLoads + row.widgetLoads,
            conversations: totals.conversations + row.conversations,
        }),
        { widgetLoads: 0, conversations: 0 },
    );

export const normalizeReport = (response = {}, page = 1, perPage) => {
    const data = unwrapData(response);
    const source = data && !Array.isArray(data) ? data : {};
    const urls = unwrapList(source, ['urls', 'pages', 'by_url']).map(normalizeMetricRow);
    const series = unwrapList(source, [
        'series',
        'timeline',
        'over_time',
        'buckets',
    ]).map(normalizeSeriesRow);
    const rawTotals = source.summary || source.totals || {};
    const paginated = paginateUrls(urls, page, perPage || urls.length || 1);
    const seriesTotals = sumMetrics(series);
    const fallbackTotals =
        seriesTotals.widgetLoads || seriesTotals.conversations
            ? seriesTotals
            : sumMetrics(urls);

    const totalFor = (keys, fallback) => {
        const value = firstDefined(rawTotals, keys);
        return value !== undefined ? toCount(value) : fallback;
    };

    return {
        urls: paginated.urls,
        series,
        totals: {
            widgetLoads: totalFor(
                ['widgetLoads', 'widget_loads', 'loads'],
                fallbackTotals.widgetLoads,
            ),
            conversations: totalFor(
                ['conversations', 'widget_conversations'],
                fallbackTotals.conversations,
            ),
        },
        pagination: paginated.pagination,
    };
};

export const normalizeAccounts = (response = {}) =>
    unwrapList(response, ['accounts']).map((account) => ({
        id: String(firstDefined(account, ['id', 'account_id'])),
        name: firstDefined(account, ['name', 'account_name']) || '',
    }));

export const normalizeWidgets = (response = {}, accountId = null) =>
    unwrapList(response, ['widgets']).map((widget) => ({
        id: String(firstDefined(widget, ['id', 'widget_id'])),
        name: firstDefined(widget, ['name', 'widget_name']) || '',
        accountId: String(
            firstDefined(widget, ['accountId', 'account_id']) || accountId || '',
        ),
    }));

export const normalizeFilterOptions = (response = {}) => {
    const data = unwrapData(response);
    const source = data && !Array.isArray(data) ? data : response;

    return {
        accounts: normalizeAccounts(source),
        widgets: normalizeWidgets(source),
    };
};

export const conversionRate = (widgetLoads, conversations) => {
    if (!widgetLoads) {
        return 0;
    }

    return (conversations / widgetLoads) * 100;
};
